import axios from "axios";
import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import { jwtDecode } from "jwt-decode";

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL + "/";
axios.defaults.withCredentials = true;

// For handling multiple requests while refreshing the token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor for API calls
axios.interceptors.request.use(
  async (config) => {
    const authStore = useAuthStore();

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };
    const token = authStore.user?.access_token || getCookie("authToken");

    if (token != null) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };
    } else if (authStore.preAuthToken != null) {
      // Allow only safe/specific pre-auth endpoints during 2FA/FIDO challenges
      const allowedPreAuthPaths = [
        "/users/two-factor-auth/verify/",
        "/users/two-factor-auth/verify-temp/",
        "/webauthn/register/begin",
        "/webauthn/register/finish",
      ];
      const isAllowed =
        (config.url && allowedPreAuthPaths.some((path) => config.url.includes(path))) ||
        (config.url && config.url.match(/\/users\/two-factor-auth\/\d+$/));

      if (isAllowed) {
        config.headers = {
          Authorization: `Bearer ${authStore.preAuthToken}`,
          Accept: "application/json",
        };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors and refresh the token
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      console.log(
        "axios.js: Received 401 Unauthorized for",
        originalRequest?.url
      );

      const authStore = useAuthStore();
      if (authStore.isLoggingOut || (originalRequest?.url && originalRequest.url.includes("/logout"))) {
        console.log("axios.js: 401 received during logout or logout endpoint. Skipping refresh and expired redirect.");
        return Promise.reject(error);
      }

      // If the refresh request itself failed with 401, session is completely expired. Prevent deadlock.
      if (
        originalRequest?._isRefreshRequest ||
        (originalRequest?.url &&
          (originalRequest.url.includes("/token/refresh") ||
            originalRequest.url.includes("/users/token/refresh")))
      ) {
        console.log("axios.js: Token refresh request failed with 401. Logging out.");
        isRefreshing = false;
        processQueue(error, null);
        logout(true);
        return Promise.reject(error);
      }

      if (originalRequest?._retry) {
        console.log("axios.js: Request already retried, logging out.");
        logout(true);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
        return null;
      };
      const token = authStore.user?.access_token || getCookie("authToken");

      if (!token || typeof token !== "string") {
        console.log("axios.js: No access token available, logging out.");
        logout(true);
        return Promise.reject(error);
      }

      let jwtPayload;
      try {
        jwtPayload = jwtDecode(token);
      } catch (decodeError) {
        console.error("axios.js: Failed to decode token, logging out.", decodeError);
        logout(true);
        return Promise.reject(error);
      }

      if (!jwtPayload?.jwt_refresh_token) {
        console.log("axios.js: No refresh token in payload, logging out.");
        logout(true);
        return Promise.reject(error);
      }

      const data = {
        id: authStore.user?.id || jwtPayload?.user_id,
        access_token: token,
        refresh_token: jwtPayload.jwt_refresh_token,
      };

      originalRequest._retry = true;
      isRefreshing = true;

      console.log("axios.js: Attempting to refresh token.");
      return new Promise((resolve, reject) => {
        axios
          .post("/users/token/refresh", data, { _isRefreshRequest: true })
          .then(({ data }) => {
            if (data && (data.status === true || data.success === true) && data.user && data.user.access_token) {
              console.log("axios.js: Token refreshed successfully.");
              authStore.setUser(data.user);
              axios.defaults.headers.common["Authorization"] =
                "Bearer " + data.user.access_token;
              originalRequest.headers["Authorization"] =
                "Bearer " + data.user.access_token;
              processQueue(null, data.user.access_token);
              resolve(axios(originalRequest));
            } else {
              console.error("axios.js: Token refresh returned status false or missing user", data);
              const refreshErr = new Error((data && data.message) || "Token refresh failed");
              processQueue(refreshErr, null);
              logout(true);
              reject(refreshErr);
            }
          })
          .catch((err) => {
            console.error(
              "axios.js: Failed to refresh token. Logging out.",
              err
            );
            processQueue(err, null);
            logout(true);
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

const logout = (showExpired = true) => {
  const authStore = useAuthStore();

  authStore.resetState();
  document.cookie =
    "authToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;Secure";
  document.cookie =
    "authToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  localStorage.removeItem("auth");
  localStorage.removeItem("user");
  localStorage.removeItem("organisation");
  localStorage.removeItem("userId");
  localStorage.removeItem("vertex_auth");
  sessionStorage.clear();

  const query = {};
  if (router.currentRoute.value.query?.redirect_url) {
    query.redirect_url = router.currentRoute.value.query.redirect_url;
  }

  if (showExpired) {
    query.logout = "true";
    query.expired = "true";
  }

  const currentPath = router.currentRoute.value.path;
  if (currentPath !== "/login") {
    router.replace({ path: "/login", query }).catch(() => {
      const queryString = new URLSearchParams(query).toString();
      window.location.href = `/login${queryString ? `?${queryString}` : ""}`;
    });
  } else if (showExpired) {
    router.replace({
      path: "/login",
      query: { ...router.currentRoute.value.query, ...query },
    });
  }
};

export default {
  install: (app) => {
    app.config.globalProperties.$axios = axios;
  },
};
